const BUILD_INFO_URL = "/build.json";
const BUILD_DOCUMENT_URL = "/";
const RELOAD_KEY_PREFIX = "forkto.app-update-reload:";
const DEFAULT_MIN_CHECK_INTERVAL_MS = 30 * 1000;
const DEFAULT_READINESS_ATTEMPTS = 3;
const DEFAULT_READINESS_RETRY_DELAY_MS = 4 * 1000;

function getSessionStorage(windowRef) {
    try {
        return windowRef?.sessionStorage;
    } catch {
        return undefined;
    }
}

function isOffline(windowRef) {
    return windowRef?.navigator?.onLine === false;
}

export function markBuildReloadAttempted(storage, currentBuildId, latestBuildId) {
    if (!latestBuildId || latestBuildId === currentBuildId) return false;

    const key = `${RELOAD_KEY_PREFIX}${latestBuildId}`;
    try {
        if (storage?.getItem(key) === "1") {
            return false;
        }
        storage?.setItem(key, "1");
    } catch {
        // Storage can be unavailable in private browsing or restricted webviews.
    }
    return true;
}

export async function fetchLatestBuildInfo({
    fetchImpl = typeof fetch !== "undefined" ? fetch : undefined,
    buildInfoUrl = BUILD_INFO_URL
} = {}) {
    if (!fetchImpl) return null;

    const separator = buildInfoUrl.includes("?") ? "&" : "?";
    const response = await fetchImpl(`${buildInfoUrl}${separator}t=${Date.now()}`, {
        cache: "no-store",
        headers: {
            "Cache-Control": "no-cache"
        }
    });

    if (!response?.ok) return null;
    return response.json();
}

function parseTagAttributes(tag) {
    const attributes = {};
    const attributePattern = /([^\s=/>]+)\s*=\s*(["'])(.*?)\2/g;
    for (const match of tag.matchAll(attributePattern)) {
        attributes[match[1].toLowerCase()] = match[3];
    }
    return attributes;
}

export function readBuildIdFromHtml(html) {
    if (typeof html !== "string") return null;

    for (const tag of html.match(/<meta\b[^>]*>/gi) ?? []) {
        const attributes = parseTagAttributes(tag);
        if (attributes.name === "forkto-build-id") {
            return attributes.content || null;
        }
    }
    return null;
}

export function readEntryAssetUrlsFromHtml(html) {
    if (typeof html !== "string") return [];

    const assetUrls = new Set();
    for (const tag of html.match(/<(?:link|script)\b[^>]*>/gi) ?? []) {
        const attributes = parseTagAttributes(tag);
        const assetUrl = attributes.src || attributes.href;
        if (!assetUrl || !/^(?:\/|\.\/)/.test(assetUrl)) continue;
        if (!/\.(?:css|js)(?:[?#]|$)/i.test(assetUrl)) continue;
        assetUrls.add(assetUrl);
    }
    return [...assetUrls];
}

function assetResponseMatchesType(response, assetUrl) {
    if (!response?.ok) return false;

    const contentType = response.headers?.get?.("content-type");
    if (!contentType) return true;
    if (/\.css(?:[?#]|$)/i.test(assetUrl)) return contentType.includes("text/css");
    if (/\.js(?:[?#]|$)/i.test(assetUrl)) return contentType.includes("javascript");
    return true;
}

export async function fetchDeploymentReadiness({
    expectedBuildId,
    fetchImpl = typeof fetch !== "undefined" ? fetch : undefined,
    documentUrl = BUILD_DOCUMENT_URL
} = {}) {
    if (!fetchImpl || !expectedBuildId) return false;

    const separator = documentUrl.includes("?") ? "&" : "?";
    const response = await fetchImpl(`${documentUrl}${separator}t=${Date.now()}`, {
        cache: "no-store",
        headers: {
            "Cache-Control": "no-cache"
        }
    });
    if (!response?.ok) return false;

    const html = await response.text();
    if (readBuildIdFromHtml(html) !== expectedBuildId) return false;

    const assetUrls = readEntryAssetUrlsFromHtml(html);
    if (assetUrls.length === 0) return false;

    const assetResponses = await Promise.all(
        assetUrls.map((assetUrl) =>
            fetchImpl(assetUrl, {
                method: "HEAD",
                cache: "no-store",
                headers: {
                    "Cache-Control": "no-cache"
                }
            })
        )
    );
    return assetResponses.every((assetResponse, index) =>
        assetResponseMatchesType(assetResponse, assetUrls[index])
    );
}

function wait(delayMs) {
    return new Promise((resolve) => setTimeout(resolve, delayMs));
}

let installedCheckForUpdate = null;

export function checkForAppUpdate(options = {}) {
    if (!installedCheckForUpdate) {
        return Promise.resolve(false);
    }
    return installedCheckForUpdate(options);
}

export function installAppUpdateRecovery({
    windowRef = typeof window !== "undefined" ? window : undefined,
    documentRef = typeof document !== "undefined" ? document : undefined,
    fetchImpl = typeof fetch !== "undefined" ? fetch : undefined,
    storage,
    reload = () => windowRef?.location?.reload(),
    addBreadcrumb,
    currentBuildId = import.meta.env.VITE_BUILD_ID,
    enabled = import.meta.env.PROD,
    minCheckIntervalMs = DEFAULT_MIN_CHECK_INTERVAL_MS,
    readinessAttempts = DEFAULT_READINESS_ATTEMPTS,
    readinessRetryDelayMs = DEFAULT_READINESS_RETRY_DELAY_MS,
    waitImpl = wait,
    checkOnInstall = true
} = {}) {
    if (!enabled || !windowRef?.addEventListener || !currentBuildId) {
        return () => {};
    }

    const reloadStorage = storage ?? getSessionStorage(windowRef);
    let lastCheckedAt = 0;
    let checkInFlight = null;

    const checkForUpdate = async ({ force = false } = {}) => {
        if (isOffline(windowRef)) return false;

        const now = Date.now();
        if (!force && now - lastCheckedAt < minCheckIntervalMs) {
            return false;
        }
        lastCheckedAt = now;

        if (checkInFlight) return checkInFlight;

        checkInFlight = fetchLatestBuildInfo({ fetchImpl })
            .then(async (latest) => {
                const latestBuildId = latest?.buildId;
                if (!latestBuildId || latestBuildId === currentBuildId) {
                    return false;
                }

                let deploymentReady = false;
                for (let attempt = 0; attempt < readinessAttempts; attempt += 1) {
                    await waitImpl(readinessRetryDelayMs);
                    const confirmedBuild = await fetchLatestBuildInfo({ fetchImpl });
                    if (confirmedBuild?.buildId !== latestBuildId) {
                        return false;
                    }
                    if (
                        await fetchDeploymentReadiness({
                            expectedBuildId: latestBuildId,
                            fetchImpl
                        })
                    ) {
                        deploymentReady = true;
                        break;
                    }
                }

                if (!deploymentReady) {
                    addBreadcrumb?.({
                        category: "app.update",
                        level: "debug",
                        message: "Deployed build is not ready to load",
                        data: { latestBuildId }
                    });
                    return false;
                }

                if (!markBuildReloadAttempted(reloadStorage, currentBuildId, latestBuildId)) {
                    return false;
                }

                addBreadcrumb?.({
                    category: "app.update",
                    level: "info",
                    message: "Reloading after deployed build changed",
                    data: {
                        currentBuildId,
                        latestBuildId
                    }
                });
                reload();
                return true;
            })
            .catch((error) => {
                addBreadcrumb?.({
                    category: "app.update",
                    level: "debug",
                    message: "Build update check failed",
                    data: { error: error?.message }
                });
                return false;
            })
            .finally(() => {
                checkInFlight = null;
            });

        return checkInFlight;
    };

    const onVisible = () => {
        if (!documentRef || documentRef.visibilityState === "visible") {
            void checkForUpdate();
        }
    };
    const onOnline = () => {
        void checkForUpdate({ force: true });
    };

    windowRef.addEventListener("focus", onVisible);
    windowRef.addEventListener("online", onOnline);
    documentRef?.addEventListener?.("visibilitychange", onVisible);

    installedCheckForUpdate = checkForUpdate;
    if (checkOnInstall) {
        void checkForUpdate({ force: true });
    }

    return () => {
        windowRef.removeEventListener("focus", onVisible);
        windowRef.removeEventListener("online", onOnline);
        documentRef?.removeEventListener?.("visibilitychange", onVisible);
        if (installedCheckForUpdate === checkForUpdate) {
            installedCheckForUpdate = null;
        }
    };
}
